import { useEffect, useMemo, useState } from 'react';
import { LoadedClient, toDashboardClient } from '../tasks/clientRecord';
import { fetchCollaboratorClients } from '../tasks/taskSources';
import { DashboardClient } from '../tasks/types';
import { TASK_CACHE_MS } from '../tasks/useClientTasks';

export interface CollaboratorClients {
  clients: DashboardClient[];
  status: 'idle' | 'loading' | 'ready' | 'error';
  retry: () => void;
}

const cache = new Map<string, { loadedAt: number; clients: LoadedClient[] }>();

/**
 * Clients the coach collaborates on but doesn't lead, so Today can show the
 * sessions the coach delivers for them. Kept apart from the task hook: their
 * tasks belong to the lead coach and stay out of this coach's Actions.
 */
export function useCollaboratorClients(coachId: string | null, enabled: boolean): CollaboratorClients {
  const key = enabled && coachId ? coachId : null;
  const cached = key ? cache.get(key) : undefined;
  const fresh = cached && Date.now() - cached.loadedAt < TASK_CACHE_MS ? cached.clients : null;
  const [loaded, setLoaded] = useState<LoadedClient[] | null>(fresh);
  const [status, setStatus] = useState<CollaboratorClients['status']>(!key ? 'idle' : fresh ? 'ready' : 'loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) {
      setLoaded(null);
      setStatus('idle');
      return undefined;
    }
    const entry = cache.get(key);
    if (attempt === 0 && entry && Date.now() - entry.loadedAt < TASK_CACHE_MS) {
      setLoaded(entry.clients);
      setStatus('ready');
      return undefined;
    }
    let cancelled = false;
    setStatus('loading');
    fetchCollaboratorClients(key)
      .then((clients) => {
        cache.set(key, { loadedAt: Date.now(), clients });
        if (cancelled) return;
        setLoaded(clients);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Dashboard: collaborator clients failed to load', error);
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [key, attempt]);

  const clients = useMemo(() => {
    const now = new Date();
    // A coach listed as a collaborator on a client they also lead is already in the task hook's list.
    return (loaded ?? []).map((client) => toDashboardClient(client, now)).filter((client) => client.coachId !== key);
  }, [loaded, key]);

  return { clients, status, retry: () => setAttempt((n) => n + 1) };
}
